import React, { useState, useCallback, useEffect, useRef } from "react";
import { config } from "@triare/auth-redux";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import { Button, Segmented, message, Spin } from "antd";
import {
  ExclamationCircleFilled,
  EyeOutlined,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Survey } from "../../../../types/survey";
import { SurveyBuilder } from "../../../Common/Survey/SurveyBuilder";
import {
  useSurveyCreateFull,
  useTemplateCreate,
} from "../../../../hooks/api/surveys";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import { transformSurveyToApi } from "../../../../utils/transformSurveyToApi";
import { SurveyPreview } from "../../../Common/SurveyPreview";
import { useNavigate } from "react-router";
import { cloneSurveyWithNewIds } from "../../../../utils/cloneSurveyForTemplate";
import Loading from "../../../Common/Loading";
import { useMessageError } from "../../../../hooks/common";
import { useUnsavedChangesGuard } from "../../../../utils/useUnsavedChangesGuard";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import isEqual from "lodash/isEqual";

const createEmptySurvey = (): Survey => ({
  status: "draft",
  title: "",
  items: [],
});

const normalizeForCompare = (survey: Survey) => ({
  title: survey.title,
  items: survey.items,
  status: survey.status,
});

export default function TemplateCreateContent(): React.ReactNode | null {
  const [mode, setMode] = useState<"add" | "preview">("add");
  const { setBreadcrumbs } = useBreadcrumbs();
  const { open, contextHolder } = useSimpleModal();
  const navigate = useNavigate();
  const [surveyData, setSurveyData] = useState<Survey>(createEmptySurvey());
  const surveyCreate = useSurveyCreateFull();
  const templateCreate = useTemplateCreate();
  const [baselineSurvey, setBaselineSurvey] =
    useState<Survey>(createEmptySurvey());

  const isDirty = !isEqual(
    normalizeForCompare(surveyData),
    normalizeForCompare(baselineSurvey),
  );

  useUnsavedChangesGuard(isDirty, () => { }, {
    open: (config) =>
      open({
        ...config,
        icon: <ExclamationCircleFilled />,
        okButtonProps: { danger: true },
      }),
  });

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Template", href: "/templates" },
      { title: "Create" },
    ]);
  }, []);

  const handleSurveyChange = useCallback((survey: Survey) => {
    setSurveyData(survey);
  }, []);

  const handleSave = useCallback(async () => {
    const apiData = transformSurveyToApi({
      ...surveyData,
      status: "draft",
    });

    try {
      const res = await templateCreate.fetch(apiData);

      if (res?.id) {
        message.success("Template saved");
        setBaselineSurvey(surveyData);

        setTimeout(() => {
          navigate(`/templates`);
        }, 0);
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to save template");
      console.error(err);
    }
  }, [surveyData, templateCreate, navigate]);

  const handleSaveSurvey = useCallback(async () => {
    const clonedSurvey = cloneSurveyWithNewIds(surveyData);

    const apiData = transformSurveyToApi({
      ...clonedSurvey,
      status: "draft",
    });

    try {
      const res = await surveyCreate.fetch(apiData);

      if (res?.id) {
        message.success("Template created");
        // navigate(`/templates`);
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to save template");
      console.error(err);
    }
  }, [surveyData, surveyCreate, navigate]);

  const handleModeChange = useCallback((value: "add" | "preview") => {
    setMode(value);
  }, []);

  useMessageError([templateCreate]);
  useMessageError([templateCreate]);

  return (
    <SinglePageContainer
      title="Add template"
      className="pagePadding"
      extra={
        <>
          <Button onClick={handleSave} loading={templateCreate.loading}>
            Save
          </Button>
          <Button
            type="primary"
            onClick={handleSaveSurvey}
            loading={surveyCreate.loading}
          >
            Create survey
          </Button>
        </>
      }
      style={{ backgroundColor: "transparent" }}
    >
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div style={{ width: 880 }}>
          <Segmented
            size="large"
            value={mode}
            onChange={(value) => handleModeChange(value as "add" | "preview")}
            options={[
              { label: "Add", value: "add", icon: <PlusCircleOutlined /> },
              { label: "Preview", value: "preview", icon: <EyeOutlined /> },
            ]}
          />
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        {mode === "add" && (
          <SurveyBuilder
            isTemplate
            initialSurvey={surveyData}
            onChange={handleSurveyChange}
          />
        )}
        {mode === "preview" && (
          <SurveyPreview
            isTemplate
            survey={surveyData}
            readOnly={false}
            onSubmit={(answers) => console.log("Answers:", answers)}
          />
        )}
      </div>
      {surveyCreate.loading || templateCreate.loading ? (
        <Loading absolute />
      ) : null}
      {contextHolder}
    </SinglePageContainer>
  );
}
